'use client';

import React, {
  useState,
  MouseEvent,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { Box, Select, Typography, Option, Autocomplete, CircularProgress } from 'src/components';
import {
  faCaretDown,
  faSearch,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { useEncounters } from 'src/context/providers/EncountersProvider';
import { EncountersActionTypes } from 'src/context/reducers/encountersReducer';
import { VolumeType, CodeInfo } from 'src/context/reducers';
import { useDebounce } from 'use-debounce';
import {
  useGetCodeSuggestionsQuery,
  useGetModifiersInfiniteQuery,
  Modifier,
} from 'src/api/redux/codes';
import { useInfiniteScroll } from 'src/hooks/common';
import { OptionType } from 'src/components/PulseUI/Autocomplete';
import { faRegistered } from '@fortawesome/pro-regular-svg-icons';
import { isCptOrHcpcs } from 'src/components/Encounters/utils';

interface CodeGroupProps {
  groupId: string;
  hideHeader?: boolean;
}

const CodeGroup = ({
  groupId,
  hideHeader = false,
}: CodeGroupProps): React.ReactElement => {
  const { state, dispatch } = useEncounters();

  const { volumeType } = state;

  const currentGroup = useMemo(
    () => state.billingCodes.groups.find((g) => g.id === groupId),
    [state.billingCodes.groups, groupId]
  );

  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue] = useDebounce(inputValue, 500);

  // Modifier picker state
  const [modifierPickerCodeId, setModifierPickerCodeId] = useState<
    string | null
  >(null);
  const [modifierSearchValue, setModifierSearchValue] = useState('');
  const modifierPickerRef = useRef<HTMLDivElement>(null);

  const { data: searchResultsRaw, isLoading } = useGetCodeSuggestionsQuery({
    q: debouncedInputValue,
  });
  const searchResults = searchResultsRaw && 'results' in searchResultsRaw ? searchResultsRaw : Array.isArray(searchResultsRaw) ? { results: searchResultsRaw } : undefined;

  const [debouncedModifierSearch] = useDebounce(modifierSearchValue, 300);

  const {
    data: modifiersData,
    isFetching: isModifiersFetching,
    hasNextPage,
    fetchNextPage,
  } = useGetModifiersInfiniteQuery(
    { q: debouncedModifierSearch || undefined },
    { skip: !modifierPickerCodeId }
  );

  const { fetchTriggerRef } = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetching: isModifiersFetching,
    fetchNextPage,
  });

  const modifiers = useMemo(() => {
    if (!modifiersData?.pages) return [];
    return modifiersData.pages.flatMap((page) => page.results ?? []);
  }, [modifiersData]);

  // Close modifier picker on click outside
  useEffect(() => {
    if (!modifierPickerCodeId) return;

    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (
        modifierPickerRef.current &&
        !modifierPickerRef.current.contains(event.target as Node)
      ) {
        setModifierPickerCodeId(null);
        setModifierSearchValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [modifierPickerCodeId]);

  const formattedSuggestionOptions = useMemo<OptionType[]>(() => {
    if (!searchResults?.results) return [];

    return searchResults.results
      .map((result): OptionType | null => {
        const code_name = result.code_name;
        const code = result.code;
        const description = result.description;
        const code_type = result.code_type;

        if (!code_name || !code || !code_type) return null;

        const cptMarker = code_type === 'CPT' ? '®' : '';
        let label = `${code} (${code_type}${cptMarker})`;
        if (description) label += ` - ${description}`;

        return { value: code_name, label };
      })
      .filter((option): option is OptionType => option !== null);
  }, [searchResults]);

  const handleOperatorChange = (
    _event: MouseEvent<Element> | null,
    value: string | null
  ) => {
    if (value && (value === 'ANY' || value === 'ALL')) {
      dispatch({
        type: EncountersActionTypes.SET_GROUP_OPERATOR,
        payload: { groupId, operator: value },
      });
    }
  };

  const handleInverseChange = (
    _event: MouseEvent<Element> | null,
    value: string | null
  ) => {
    if (value && (value === 'INCLUDES' || value === 'EXCLUDES')) {
      dispatch({
        type: EncountersActionTypes.SET_GROUP_INVERSE,
        payload: { groupId, inverse: value === 'EXCLUDES' },
      });
    }
  };

  const handleCodeDelete = useCallback(
    (codeId: string) => {
      dispatch({
        type: EncountersActionTypes.REMOVE_CODE_FROM_GROUP,
        payload: { groupId, codeId },
      });
    },
    [dispatch, groupId]
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
    },
    [setInputValue]
  );

  const handleModifierSelect = useCallback(
    (codeId: string, modifier: string, modifierDescription?: string) => {
      dispatch({
        type: EncountersActionTypes.SET_CODE_MODIFIER,
        payload: { groupId, codeId, modifier, modifierDescription },
      });
      setModifierPickerCodeId(null);
      setModifierSearchValue('');
    },
    [dispatch, groupId]
  );

  const handleModifierDelete = useCallback(
    (codeId: string, modifier: string) => {
      dispatch({
        type: EncountersActionTypes.REMOVE_CODE_MODIFIER,
        payload: { groupId, codeId, modifier },
      });
    },
    [dispatch, groupId]
  );

  const selectDisabled = state.volumeType !== VolumeType.ENCOUNTERS;
  const emptyText = isLoading
    ? 'Loading...'
    : debouncedInputValue
    ? 'No matching codes found'
    : 'Type to search for codes';

  if (!currentGroup) return null;

  const operatorText = currentGroup.operator === 'ANY' ? 'or' : 'and';
  const isTotalClaimsView = volumeType === VolumeType.TOTAL_CLAIMS;
  const canShowModifiersForVolumeType = !isTotalClaimsView;

  return (
    <Box className="flex flex-col gap-2 p-4 border border-neutral-300 rounded-lg w-full min-h-[156px]">
      {!hideHeader && (
        <>
          <Typography className="self-start font-bold text-sm">
            Code Group
          </Typography>
          <Typography className="text-tertiary text-xs">
            {canShowModifiersForVolumeType
              ? 'Supports CPT®, HCPCS, ICD10, ICD10-P, DRG and Modifiers *'
              : 'Supports CPT®, HCPCS, ICD10, ICD10-P and DRG'}
          </Typography>
        </>
      )}

      <Box className="flex flex-col w-full pt-3 space-y-3">
        <Box className="flex items-center w-full space-x-2">
          {!isTotalClaimsView && (
            <Select
              value={currentGroup.inverse ? 'EXCLUDES' : 'INCLUDES'}
              onChange={handleInverseChange}
              disabled={selectDisabled}
              className={`
              w-36
              px-1.5 h-10
              !rounded-full text-base
              focus:border-sky-600 focus:border-1
              focus:shadow-[0px_0px_2px_2px_theme(colors.sky.100)]
              ${
                isTotalClaimsView
                  ? 'bg-level-2 text-neutral-400 !hover:border-none'
                  : 'bg-white text-primary hover:bg-neutral-50'
              }
            `}
              data-testid={`code-group-inverse-select-${groupId}`}
              variant="outlined"
              endDecorator={
                <Icon
                  icon={faCaretDown}
                  className={`${
                    isTotalClaimsView ? 'text-neutral-400' : 'text-neutral-600'
                  }`}
                  size="lg"
                />
              }
            >
              <Option value="INCLUDES">INCLUDES</Option>
              <Option value="EXCLUDES">EXCLUDES</Option>
            </Select>
          )}

          <Select
            value={currentGroup.operator}
            onChange={handleOperatorChange}
            disabled={selectDisabled}
            className={`
              w-24
              px-1.5 h-10
              !rounded-full text-base
              focus:border-sky-600 focus:border-1
              focus:shadow-[0px_0px_2px_2px_theme(colors.sky.100)]
              ${
                isTotalClaimsView
                  ? 'bg-level-2 text-neutral-400 !hover:border-none'
                  : 'bg-white text-primary hover:bg-neutral-50'
              }
            `}
            data-testid={`code-group-select-${groupId}`}
            variant="outlined"
            endDecorator={
              <Icon
                icon={faCaretDown}
                className={`${
                  isTotalClaimsView ? 'text-neutral-400' : 'text-neutral-600'
                }`}
                size="lg"
              />
            }
          >
            <Option value="ANY">ANY</Option>
            <Option value="ALL">ALL</Option>
          </Select>
          <Typography className="text-text-primary text-sm pl-2">
            of these:
          </Typography>
        </Box>

        <Box className="relative w-full">
          <Autocomplete
            options={formattedSuggestionOptions}
            value={currentGroup.codes.map((code) => code.id)}
            onChange={(values) => {
              const prevCodeIds = currentGroup.codes.map((code) => code.id);

              const added = values.find((v) => !prevCodeIds.includes(v));
              const removed = prevCodeIds.find((v) => !values.includes(v));

              if (added) {
                const selectedCodeObject = searchResults?.results?.find(
                  (result) => result.code_name === added
                );

                if (selectedCodeObject) {
                  const codeToAdd: CodeInfo = {
                    id: selectedCodeObject.code_name,
                    code: selectedCodeObject.code,
                    context_type: selectedCodeObject.code_type.toLowerCase(),
                    category: selectedCodeObject.category,
                    description: selectedCodeObject.description,
                  };

                  dispatch({
                    type: EncountersActionTypes.ADD_CODE_TO_GROUP,
                    payload: { groupId, code: codeToAdd },
                  });
                }
              } else if (removed) {
                dispatch({
                  type: EncountersActionTypes.REMOVE_CODE_FROM_GROUP,
                  payload: { groupId, codeId: removed },
                });
              }
            }}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            placeholder="Search by code or drug name..."
            loading={isLoading}
            emptyOptionsText={emptyText}
            className="
              w-full
              pl-3 rounded-full h-10
              focus-within:border-sky-600 focus-within:border-1
              focus-within:shadow-[0px_0px_2px_2px_theme(colors.sky.100)]
            "
            dropdownClassName="w-full [&_span]:text-xs [&_div.flex.items-center.gap-2]:items-start"
            hideOptionsHeader
            hideEndDecorator
            data-testid={`code-group-autocomplete-${groupId}`}
            variant="outlined"
            startDecorator={
              <Icon icon={faSearch} className="text-neutral-600" size="sm" />
            }
          />

          {inputValue && (
            <Box
              className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setInputValue('');
                const syntheticEvent = {
                  target: { value: '' },
                } as React.ChangeEvent<HTMLInputElement>;
                handleInputChange(syntheticEvent);
              }}
            >
              <Icon
                icon={faXmark}
                className="w-3 h-3 text-neutral-500 hover:text-neutral-700"
                data-testid={`${groupId}-clear-input`}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Selected Codes Area */}
      {currentGroup.codes.length > 0 && (
        <Box className="flex flex-wrap items-center gap-x-1 gap-y-2 pt-3">
          {currentGroup.codes.map((code, index) => {
            const codeKey = `${code.id}${
              code.modifier ? `:${code.modifier}` : ''
            }`;
            const isPickerOpen = modifierPickerCodeId === codeKey;

            return (
              <React.Fragment key={codeKey}>
                {index > 0 && (
                  <Typography
                    className="text-sm text-tertiary mx-1"
                    data-testid={`code-group-operator-text-${groupId}`}
                  >
                    {operatorText}
                  </Typography>
                )}
                <div className="relative">
                  <div
                    className="flex items-center gap-2 rounded-full border border-neutral-300 bg-white pl-3 pr-2 py-1"
                    data-testid={`code-pill-${code.id}`}
                  >
                    <span className="flex items-center gap-0.5 text-sm text-primary font-medium whitespace-nowrap">
                      {code.code || code.id}
                      {code.context_type?.toLowerCase() === 'cpt' && (
                        <Icon
                          icon={faRegistered}
                          className="text-[0.45rem] mb-1 text-neutral-700"
                        />
                      )}
                    </span>
                    <span className="text-xs text-neutral-400 uppercase">
                      {code.context_type}
                    </span>
                    {canShowModifiersForVolumeType && code.modifier && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setModifierPickerCodeId(codeKey);
                          setModifierSearchValue('');
                        }}
                        className="bg-teal-50 text-teal-700 border border-teal-300 rounded-full px-2 text-xs leading-5 cursor-pointer hover:bg-teal-100"
                        data-testid={`code-pill-modifier-${code.modifier}`}
                        aria-label={`Change modifier ${code.modifier}`}
                      >
                        {code.modifier}
                      </button>
                    )}
                    {canShowModifiersForVolumeType &&
                      !code.modifier &&
                      isCptOrHcpcs(code.context_type) && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setModifierPickerCodeId(codeKey);
                            setModifierSearchValue('');
                          }}
                          className="border border-dashed border-neutral-300 rounded-full px-2 text-xs leading-5 text-neutral-400 italic cursor-pointer hover:border-neutral-400 hover:text-neutral-500"
                          data-testid="code-pill-add-modifier"
                          aria-label="Add modifier"
                        >
                          + modifier
                        </button>
                      )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCodeDelete(code.id);
                      }}
                      className="text-neutral-400 hover:text-neutral-600"
                      data-testid={`code-pill-delete-${code.id}`}
                      aria-label={`Remove code ${code.code || code.id}`}
                    >
                      <Icon icon={faXmark} className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {isPickerOpen && (
                    <div
                      ref={modifierPickerRef}
                      className="absolute top-full left-0 z-50 mt-1 w-64 bg-white border border-neutral-200 rounded-lg shadow-lg"
                      data-testid={`modifier-picker-${code.id}`}
                    >
                      <div className="p-2 border-b border-neutral-100">
                        <input
                          type="text"
                          value={modifierSearchValue}
                          onChange={(e) =>
                            setModifierSearchValue(e.target.value)
                          }
                          placeholder="Search modifiers..."
                          className="w-full px-3 py-1.5 text-sm border border-neutral-300 rounded-full focus:outline-none focus:border-sky-600"
                          data-testid={`modifier-picker-search-${code.id}`}
                          autoFocus
                        />
                      </div>
                      <div
                        className="max-h-48 overflow-y-auto"
                        role="listbox"
                        aria-label="Modifier options"
                      >
                        {modifiers.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-neutral-400">
                            No modifiers found
                          </div>
                        ) : (
                          modifiers.map((mod: Modifier) => {
                            const isSelected = code.modifier === mod.code;
                            return (
                              <button
                                key={mod.code}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                aria-label={`${
                                  isSelected ? 'Remove' : 'Select'
                                } modifier ${mod.code}: ${mod.description}`}
                                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                                  isSelected
                                    ? 'bg-teal-50 hover:bg-teal-100'
                                    : 'hover:bg-neutral-50'
                                }`}
                                onClick={() => {
                                  if (isSelected) {
                                    handleModifierDelete(code.id, mod.code);
                                    setModifierPickerCodeId(code.id);
                                  } else {
                                    handleModifierSelect(
                                      code.id,
                                      mod.code,
                                      mod.description
                                    );
                                  }
                                }}
                                data-testid={`modifier-option-${mod.code}`}
                              >
                                <span className="font-medium text-teal-700">
                                  {mod.code}
                                </span>
                                <span className="text-xs text-neutral-500 truncate">
                                  {mod.description}
                                </span>
                                {isSelected && (
                                  <Icon
                                    icon={faXmark}
                                    className="w-3 h-3 text-neutral-400 ml-auto shrink-0"
                                  />
                                )}
                              </button>
                            );
                          })
                        )}
                        {isModifiersFetching && (
                          <div className="px-3 py-2 flex items-center justify-center">
                            <CircularProgress size="sm" color="neutral" />
                          </div>
                        )}
                        <div
                          ref={fetchTriggerRef}
                          className="h-1"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </Box>
      )}
      {canShowModifiersForVolumeType && (
        <span className="text-[9px] text-tertiary">
          * Modifiers are only supported for CPT and HCPCS codes.
        </span>
      )}
    </Box>
  );
};

export default CodeGroup;
